'use client';

import { ComponentProps } from 'react';

import { Button } from '@/ui';
import { cn } from '@lib';
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react';
import { DayPicker } from 'react-day-picker';

import { getSelectedDates } from './helpers';

export type CalendarProps = ComponentProps<typeof DayPicker>;

function Calendar({ className, classNames, showOutsideDays = true, ...props }: CalendarProps) {
  const { isSameDay } = getSelectedDates('selected' in props && props.selected);

  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn('p-3', className)}
      classNames={{
        caption_label: 'text-sm font-medium relative',
        day: 'text-center text-sm p-0 relative first:data-[selected="true"]:rounded-l-lg last:data-[selected="true"]:rounded-r-lg focus-within:relative focus-within:z-20 font-normal transition-[border-radius] duration-300',
        month_caption: 'flex justify-start py-2 relative items-center',
        month_grid: 'w-full flex flex-col',
        month: 'flex flex-col gap-2 w-full',
        months: 'flex flex-col gap-3 sm:flex-row',
        nav: 'gap-1 absolute z-10 right-3 top-3 py-px flex items-center',
        outside: 'day-outside text-muted-foreground opacity-50',
        range_start: cn('range-start', !isSameDay && 'rounded-r-none'),
        range_middle: 'bg-primary/50! range-middle rounded-none',
        range_end: cn('range-end', !isSameDay && 'rounded-l-none'),
        selected:
          'bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground rounded-lg',
        today: 'ring-input ring-1 ring-inset rounded-lg',
        week: 'flex justify-between mt-2',
        weekday: 'text-muted-foreground rounded-lg w-10 font-normal text-[0.8rem]',
        weekdays: 'flex justify-between',
        hidden: 'invisible',
        ...classNames,
      }}
      components={{
        Button: (props) => (
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          <Button {...(props as any)} variant="ghost" size={'smIcon'} />
        ),
        DayButton: (props) => (
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          <Button {...(props as any)} variant="ghost" size={'icon'} />
        ),

        Chevron: ({ className, ...props }) => {
          if (props.orientation === 'left') {
            return <IconChevronLeft {...props} className={cn(className, 'size-4')} />;
          }
          return <IconChevronRight {...props} className={cn(className, 'size-4')} />;
        },
      }}
      {...props}
    />
  );
}
Calendar.displayName = 'Calendar';

export { useDateRange } from './helpers';
export { Calendar };
